// @flow
import $ from "jquery";
import { isString, trim } from "voca";
import { getLogger } from "@cld/upload-widget";
import t from "common/i18n";
import { getSizeInfoFromBytes } from "utils/fileUtils";
import appConfig from "../../appConfig";
import consts from "../../uploader/common/consts";
import { isChromium } from "../../utils/domUtils";

const logger = getLogger();

const fileInputSelector = "input[type='file']";

const getLastUrlPart = (url) => url.split("/").splice(-1, 1)[0];

const getFileNameFromUrl = (url: string) => {
	let name = null;

	try {
		const fileUrl = new URL(url);
		name = getLastUrlPart(fileUrl.pathname);

		if (!name) {
			name = getLastUrlPart(decodeURIComponent(fileUrl.search));
		}
	} catch (err) {
		// silent - nothing to do
	}

	return name || url;
};

// $FlowFixMe - cant deal with jquery object with file upload plugin
const getUploadFieldFromContainer = (container: any): jQuery => (container.find
	? container.find(fileInputSelector)
	: $(container).find(fileInputSelector));

const getUploadFieldFormData = (container: any) => getUploadFieldFromContainer(container).fileupload("option", "formData");

const createCustomUploadInfo = (data: Object) => ({
	_custom: true,
	slice: () => new Blob([0, 0]), // for load-image-meta that blindly thinks an object with type and size and content type jpeg is a File instance :(
	...data,
});

const createUploadFileFromData = async (data: {
    file: any,
   }) => {
	let result = null;

	const { file } = data;
	if (isString(file)) {
		result = createCustomUploadInfo(data);
	} else {
		/*
			Due to a chrome bug similar to this  https://bugs.chromium.org/p/chromium/issues/detail?id=386992&q=component%3ABlink%3EStorage%3EFileAPI&can=2
			See this up-to date comment which describes the bug that i am talking about https://bugs.chromium.org/p/chromium/issues/detail?id=386992#c25
			I am not able to upload using the same file handler on chrome, this is why i need to clone the file
			But even if the upload succeeds chrome is not able to display images thumbnails due to in ability to read blob url
			Seems to be the same root cause which prevents chrome itself from reading the file content
		*/
		if (isChromium()) {
			const buffer = await file.arrayBuffer();
			const clonedFile = new File([buffer], file.name, {
				type: file.type,
				lastModified: file.lastModified,
			});
			result = clonedFile;
		}
		else {
			result = file;
		}
	}

	return result;
};

const FILTER_CATCH_ALL = "*!*";

const RESOURCE_TYPE_FILTERS = {
	[consts.RESOURCE_TYPES.IMAGE]: "image/*",
	[consts.RESOURCE_TYPES.VIDEO]: "video/*",
	[consts.RESOURCE_TYPES.RAW]: FILTER_CATCH_ALL,
	"audio": "audio/*",
};

const formatFileType = (type) => RESOURCE_TYPE_FILTERS[type] || `.${type}`;

const getAcceptFileTypes = (formats) => {
	const formatsArray = formats ? // ensure is array
			(Array.isArray(formats) ? formats : formats.split(",")) :
		[];

	const types = formatsArray.reduce((res, val) => {
		const trimmed = trim(val);

		const vals = /jpe?g/i.test(trimmed) ?
			// ensure jpg&jpeg
			["jpg", "jpeg"] :
			[trimmed];

		return res.concat(vals.map(formatFileType));
	}, [])
		.join("|");

	const hasCatchAll = types.includes(FILTER_CATCH_ALL);
	const hasResourceType = types.includes("*");

	return (!types.length || hasCatchAll) ?
		//in case no types or catch all (raw) -> dont use filter
		null :
		new RegExp(`(${types})${hasResourceType ? "" : "$"}`, "i");
};

const getTimestamp = () => Math.floor(new Date().getTime() / 1000);

const getUploadSource = (consoleInfo: {onConsole: boolean}) => (consoleInfo.onConsole ?
	appConfig.upload.consoleSource :
	appConfig.upload.source);

const getCoordinatesString = (coordinates: {x: number, y: number, width: number, height: number}) =>
	`${coordinates.x},${coordinates.y},${coordinates.width},${coordinates.height}`;

const getFileTooLargeMessage = (size: string | number, allowed: string | number) => {
	let msg = null;

	try {
		const sizeRes = getSizeInfoFromBytes(parseInt(size));
		const allowedRes = getSizeInfoFromBytes(parseInt(allowed));

		msg = t("uploader.errors.file_too_large", {
			size: t(`uploader.filesize.${sizeRes.text}`, sizeRes),
			allowed: t(`uploader.filesize.${allowedRes.text}`, allowedRes),
		});
	} catch (ex) {
		logger.warn("[uploader.uploadCallbacks]: failed to format file too large error message");
	}

	return msg;
};

const stringifyMetadataArray = (arr) =>
	`[${arr.map((val) => `"${val}"`).join(",")}]`;

const stringifyMetadata = (obj) => isString(obj) ? obj :
	Object.keys(obj)
		.map((key) => {
			const val = obj[key];
			return typeof val !== "undefined" ?
				`${key}=${Array.isArray(val) ? stringifyMetadataArray(val) : val}` : undefined;
		})
		.filter(Boolean)
		.join("|");

export {
	getFileNameFromUrl,
	getUploadFieldFromContainer,
	getUploadFieldFormData,
	createCustomUploadInfo,
	createUploadFileFromData,
	getAcceptFileTypes,
	getTimestamp,
	getUploadSource,
	getCoordinatesString,
	getFileTooLargeMessage,
	stringifyMetadata,
	formatFileType
};
